import urls from "../../api/urls";
import NormalAPI from "../../api";

const {
  REPORT_CONFIG_LIST,
  REPORT_CONFIG_UPSERT,
  REPORT_CONFIG_PATCH,
  REPORT_CONFIG_TOGGLE_ACTIVE,
  REPORT_CONFIG_DELETE,
  REPORT_CONFIG_UI_SCHEMA,
  DYNAMIC_REPORT_DATA,
  DYNAMIC_REPORT_DOWNLOAD,
} = urls;

// ── Helpers ───────────────────────────────────────────────────────────────────

const unwrap = (wrapped) => ({
  status: wrapped?.response?.status ?? 0,
  data: wrapped?.response?.data ?? {},
});

const handleResponse = (wrapped, okCodes, fallbackError) => {
  const { status, data } = unwrap(wrapped);
  if (okCodes.includes(status)) {
    return data;
  }
  throw new Error(data?.message || data?.error || fallbackError);
};

// ── Service ───────────────────────────────────────────────────────────────────

export const ReportConfigService = {
  getAll: async (page = 0, size = 10) => {
    const url = `${REPORT_CONFIG_LIST}?page=${page}&size=${size}`;
    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();
      const { status, data: d } = unwrap(wrapped);

      if (status === 200) {
        return {
          data: d?.data || d?.content || [],
          pagination: d?.pagination || {
            currentPage: page,
            totalPages: d?.totalPages || 1,
            totalRecords: d?.totalElements || (d?.data || d?.content || []).length,
            pageSize: size,
            hasNext: d?.hasNext ?? false,
            hasPrevious: d?.hasPrevious ?? false,
          },
        };
      }

      throw new Error(d?.message || "Failed to fetch report configs");
    } catch (error) {
      console.error("Error fetching report configs:", error);
      throw error;
    }
  },

  getUiSchema: async (reportKey) => {
    const url = `${REPORT_CONFIG_UI_SCHEMA}/${reportKey}/ui-schema`;
    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();
      return handleResponse(wrapped, [200], "Failed to fetch UI schema");
    } catch (error) {
      console.error("Error fetching UI schema:", error);
      throw error;
    }
  },

  upsert: async (payload) => {
    try {
      const wrapped = await NormalAPI.Api(REPORT_CONFIG_UPSERT).postWithHeader(payload);
      return handleResponse(wrapped, [200, 201], "Save failed");
    } catch (error) {
      console.error("Error upserting report config:", error);
      throw error;
    }
  },

  patch: async (reportKey, fields) => {
    const url = `${REPORT_CONFIG_PATCH}/${reportKey}`;
    try {
      const wrapped = await NormalAPI.Api(url).patch(fields);
      return handleResponse(wrapped, [200], "Patch failed");
    } catch (error) {
      console.error("Error patching report config:", error);
      throw error;
    }
  },

  toggleActive: async (reportKey) => {
    const url = `${REPORT_CONFIG_TOGGLE_ACTIVE}/${reportKey}/toggle-active`;
    try {
      const wrapped = await NormalAPI.Api(url).patch({});
      return handleResponse(wrapped, [200], "Toggle failed");
    } catch (error) {
      console.error("Error toggling report config:", error);
      throw error;
    }
  },

  delete: async (reportKey) => {
    const url = `${REPORT_CONFIG_DELETE}/${reportKey}`;
    try {
      const wrapped = await NormalAPI.Api(url).delete({});
      handleResponse(wrapped, [200, 204], "Delete failed");
      return true;
    } catch (error) {
      console.error("Error deleting report config:", error);
      throw error;
    }
  },

  // ── Dynamic data & download ───────────────────────────────────────────────

  getData: async (reportKey, params = {}) => {
    // Backend expects: page (0-based), size (not pageSize)
    const backendParams = { ...params };
    if (backendParams.page !== undefined) backendParams.page = backendParams.page - 1; // convert 1-based UI → 0-based backend
    if (backendParams.pageSize !== undefined) {
      backendParams.size = backendParams.pageSize;
      delete backendParams.pageSize;
    }
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(backendParams).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )
    ).toString();
    const url = `${DYNAMIC_REPORT_DATA}/${reportKey}${qs ? `?${qs}` : ""}`;
    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();
      const { status, data } = unwrap(wrapped);
      if (status === 200) {
        // Normalise the response shape coming from the backend
        const rows = data?.data ?? data?.content ?? data?.result ?? [];
        const pag = data?.pagination ?? {
          currentPage: (params.page ?? 1) - 1,
          pageSize: params.pageSize ?? 20,
          totalRecords: data?.totalElements ?? rows.length,
        };
        return { data: rows, pagination: pag };
      }
      throw new Error(data?.message || "Failed to load report data");
    } catch (error) {
      console.error("Error fetching dynamic report data:", error);
      throw error;
    }
  },

  download: async (reportKey, params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )
    ).toString();

    // Backend: GET /api/v1/dynamic-report/{reportKey}/download
    const url = `${DYNAMIC_REPORT_DOWNLOAD}/${reportKey}/download${qs ? `?${qs}` : ""}`;

    try {
      // Use API wrapper to ensure Bearer Token and other headers (like X-Request-ID) are sent
      const wrapped = await NormalAPI.Api(url).getCustomHeader();

      if (wrapped.status) {
        // Create blob from the response data (standard CSV text content)
        const blob = new Blob([wrapped.response.data], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${reportKey}-report.csv`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error(wrapped.response?.data?.message || "Download failed. Please check your filters.");
      }
    } catch (error) {
      console.error("Error during dynamic report download:", error);
      throw error;
    }
  },
  // ── Custom-URL data fetch (when pageConfig.dataUrl is set) ─────────────────

  getDataFromUrl: async (fullUrl, params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )
    ).toString();
    const url = `${fullUrl}${qs ? `?${qs}` : ""}`;
    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();
      const { status, data } = unwrap(wrapped);
      if (status === 200) {
        const rows = data?.data ?? data?.content ?? data?.result ?? [];
        const pag = data?.pagination ?? {
          currentPage: (params.page ?? 1) - 1,
          pageSize: params.pageSize ?? 20,
          totalRecords: data?.totalElements ?? rows.length,
        };
        return { data: rows, pagination: pag };
      }
      throw new Error(data?.message || "Failed to load report data");
    } catch (error) {
      console.error("Error fetching data from custom URL:", error);
      throw error;
    }
  },

  downloadFromUrl: async (fullUrl, params = {}, fileName = "report.csv") => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )
    ).toString();
    const url = `${fullUrl}${qs ? `?${qs}` : ""}`;

    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();

      if (wrapped.status) {
        const blob = new Blob([wrapped.response.data], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error(wrapped.response?.data?.message || "Download failed");
      }
    } catch (error) {
      console.error("Error using custom download URL:", error);
      throw error;
    }
  },
};
